import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { api, currentTeamId, type Me, setCurrentTeamId, type Team } from './api';

interface Session {
  me: Me;
  team: Team;
  switchTeam(id: string): void;
  /** Fetch /me again, e.g. after the team changed. */
  reload(): Promise<void>;
  logout(): Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession outside SessionProvider');
  return session;
}

type State =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'ready'; me: Me; team: Team };

/** Loads /me once. Renders `anonymous` (the login page) until there is a session. */
export function SessionProvider({
  children,
  anonymous,
}: {
  children: ReactNode;
  anonymous: (onLogin: () => void) => ReactNode;
}) {
  const [state, setState] = useState<State>({ status: 'loading' });

  const load = useCallback(async () => {
    try {
      const me = await api<Me>('GET', '/me');
      const saved = currentTeamId();
      const team = me.teams.find((t) => t.id === saved) ?? me.teams[0];
      if (!team) throw new Error('Your account has no team.');
      setCurrentTeamId(team.id);
      setState({ status: 'ready', me, team });
    } catch {
      // 401 (no session) or the API is unreachable: show the login page.
      setState({ status: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.status === 'loading') return <div className="auth" aria-busy="true" />;
  if (state.status === 'anonymous') return <>{anonymous(() => void load())}</>;

  const session: Session = {
    me: state.me,
    team: state.team,
    switchTeam(id) {
      setCurrentTeamId(id);
      const team = state.me.teams.find((t) => t.id === id);
      if (team) setState({ ...state, team });
    },
    reload: load,
    async logout() {
      await api('POST', '/auth/logout').catch(() => undefined);
      setState({ status: 'anonymous' });
    },
  };
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}
